/* eslint-disable react/prop-types */
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Card,
  CardContent,
  Grid,
  Typography,
  Box,
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@material-ui/core'
import { ContainedButton } from 'irmlib/components/Controls/Button'
import { MainRouteSubRoutePage } from '../../AppControl/RoutePage.jsx'
import { setBusy, clearBusy } from 'irmlib/containers'
import { getUser, getUserName } from '../../../redux/global.js'
import * as localGdeTransactionLite from '../../../services/firestore/localGdeTransactionsLite.js'
import * as localGdeTransactions from '../../../services/firestore/localGdeTransactions.js'
import { errorToast, successToast } from '../../../common/functions.js'
import { useNats } from '../../../providers/NatsProvider.jsx'
import { ImageUploader } from './ImageUploader.jsx'
import * as constants from '../../../irmlib/src/common/constants'
import {
  uploadAndSaveOneImage as uploadSaveAndPublishOneImage,
  updateActivityAfterBulkUpload,
  resolveBulkUploadActivity,
} from '../../../services/edgeAi/bulkActivityImagePipeline.js'

const MAX_IMAGES = 101
const MAX_MB = 10

const LocalBulkActivityImageUpload = () => {
  const { t } = useTranslation()
  const { workerId } = getUser()
  const username = getUserName()
  const { natsService } = useNats()
  const [requestId, setRequestId] = useState('')
  const [boxId, setBoxId] = useState('')
  const [images, setImages] = useState([])
  const [progress, setProgress] = useState('')
  const [validatedActivity, setValidatedActivity] = useState(null)
  const [isValidating, setIsValidating] = useState(false)
  const [isClosingBox, setIsClosingBox] = useState(false)
  const [closeDialogOpen, setCloseDialogOpen] = useState(false)
  const [totalImageCount, setTotalImageCount] = useState(0)

  const isBoxValidated = Boolean(validatedActivity)
  const normalizedRequestId = requestId.trim()
  const normalizedBoxId = boxId.trim().toUpperCase()

  const refreshTotalImageCount = async (activityId) => {
    if (!activityId) {
      setTotalImageCount(0)
      return
    }
    const imageSnapshot = await localGdeTransactionLite.getImagesByActivityId(activityId)
    setTotalImageCount(imageSnapshot?.docs?.length || 0)
  }

  const handleReset = () => {
    setRequestId('')
    setBoxId('')
    setImages([])
    setProgress('')
    setValidatedActivity(null)
    setCloseDialogOpen(false)
    setTotalImageCount(0)
  }

  const handleValidateBox = async () => {
    if (!normalizedRequestId || !normalizedBoxId) {
      errorToast({ message: t('smartCatalog.bulkNatsRequestAndBoxRequired'), title: t('toast.error') })
      return
    }

    setBusy()
    setIsValidating(true)
    try {
      const { activityId, activityData, resolvedBoxId } = await resolveBulkUploadActivity(
        normalizedRequestId,
        normalizedBoxId,
        t
      )
      const nextBoxId = (resolvedBoxId || normalizedBoxId).trim().toUpperCase()
      setRequestId(normalizedRequestId)
      setBoxId(nextBoxId)
      setValidatedActivity({
        activityId,
        activityData,
        requestId: normalizedRequestId,
        boxId: nextBoxId,
      })
      await refreshTotalImageCount(activityId)
      successToast({
        message: t('smartCatalog.bulkNatsValidateSuccess'),
        title: t('toast.success'),
      })
    } catch (e) {
      errorToast({
        message: e.message || t('toast.error'),
        title: t('toast.error'),
      })
      setValidatedActivity(null)
    } finally {
      setIsValidating(false)
      clearBusy()
    }
  }

  const handleSave = async () => {
    if (!normalizedRequestId || !normalizedBoxId) {
      errorToast({ message: t('smartCatalog.bulkNatsRequestAndBoxRequired'), title: t('toast.error') })
      return
    }
    if (!isBoxValidated) {
      errorToast({ message: t('smartCatalog.bulkNatsValidateFirst'), title: t('toast.error') })
      return
    }
    if (
      validatedActivity.requestId !== normalizedRequestId ||
      validatedActivity.boxId !== normalizedBoxId
    ) {
      setValidatedActivity(null)
      errorToast({ message: t('smartCatalog.bulkNatsNeedRevalidate'), title: t('toast.error') })
      return
    }
    if (!images.length) {
      errorToast({ message: t('smartCatalog.bulkNatsSelectFiles'), title: t('toast.error') })
      return
    }
    if (!natsService) {
      errorToast({ message: t('smartCatalog.bulkNatsNoService'), title: t('toast.error') })
      return
    }

    setBusy()
    setProgress('')
    try {
      const { activityId: id, activityData, boxId: resolvedBoxId } = validatedActivity

      const failures = []
      const publishedSummary = []
      const publishedFirestoreDocs = []
      let publishFailCount = 0
      for (let i = 0; i < images.length; i++) {
        setProgress(t('smartCatalog.bulkNatsProgress', { current: i + 1, total: images.length }))
        try {
          const uploaded = await uploadSaveAndPublishOneImage({
            natsService,
            file: images[i].file,
            activityData,
            activityId: id,
            workerId,
            username,
          })
          if (uploaded.publishFailed) {
            publishFailCount += 1
          }
          publishedSummary.push({
            fileIndex: i + 1,
            fileName: images[i].file?.name,
            imgId: uploaded.imgId,
            natsObjectName: uploaded.natsName,
            natsNuid: uploaded.natsNuid,
            messageId: uploaded.msgId || '',
            publishFailed: Boolean(uploaded.publishFailed),
          })
          publishedFirestoreDocs.push(uploaded.firestoreImage)
          // eslint-disable-next-line no-console
          console.log('[BulkUpload] saved & published (images collection doc):', uploaded.firestoreImage)
        } catch (err) {
          console.error('Bulk image failed:', err)
          failures.push({ index: i + 1, name: images[i].file?.name, error: err.message || String(err) })
        }
      }

      if (failures.length < images.length) {
        await updateActivityAfterBulkUpload(id)
        await refreshTotalImageCount(id)
      }

      if (publishedSummary.length) {
        /* eslint-disable no-console */
        console.group(`[BulkUpload] batch summary — activityId=${id}, published=${publishedSummary.length}, failed=${failures.length}`)
        console.table(publishedSummary)
        console.log('[BulkUpload] full Firestore image documents from this run:', publishedFirestoreDocs)
        console.groupEnd()
        /* eslint-enable no-console */
      }

      if (failures.length) {
        errorToast({
          message: t('smartCatalog.bulkNatsPartialFail', {
            ok: images.length - failures.length,
            fail: failures.length,
          }),
          title: t('toast.error'),
          autoHideDuration: 8000,
          isBlocking: false,
        })
      } else {
        successToast(
          {
            message: t('smartCatalog.bulkNatsComplete', {
              count: images.length,
              requestId: normalizedRequestId,
              boxId: resolvedBoxId,
            }),
            title: t('toast.success'),
          },
          6
        )
        setImages([])
      }

      if (publishFailCount > 0) {
        errorToast({
          message: t('toast.natsPublishError'),
          title: t('dataEntry.warning'),
          autoHideDuration: 8000,
          isBlocking: false,
        })
      }
      setProgress('')
    } catch (e) {
      console.error(e)
      errorToast({
        message: e.message || t('toast.error'),
        title: t('toast.error'),
      })
      setProgress('')
    } finally {
      clearBusy()
    }
  }

  const handleOpenCloseBoxDialog = () => {
    if (!isBoxValidated || totalImageCount === 0 || isClosingBox) return
    setCloseDialogOpen(true)
  }

  const handleCloseBoxDialog = () => {
    setCloseDialogOpen(false)
  }

  const handleConfirmCloseBox = async () => {
    if (!validatedActivity?.activityId) return
    setBusy()
    setIsClosingBox(true)
    try {
      await localGdeTransactions.updateActivityData(validatedActivity.activityId, {
        status: constants.activityStatus.completed,
        updatedTS: new Date().toISOString(),
        exceptionNote: '',
        isException: false,
      })
      successToast({ message: t('toast.boxScanComplete'), title: t('toast.success') }, 10)
      handleReset()
    } catch (e) {
      errorToast({
        message: e.message || t('toast.error'),
        title: t('toast.error'),
      })
    } finally {
      setIsClosingBox(false)
      setCloseDialogOpen(false)
      clearBusy()
    }
  }

  return (
    <MainRouteSubRoutePage
        mainTitle='routeTitles.edgeAi'
        subTitle='routeTitles.bulkActivityImageUpload'
        createPage={true}
        style={{ overflowX: 'auto' }}
      >
      <Card>
        <CardContent>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  label={t('smartCatalog.bulkNatsRequestId')}
                  value={requestId}
                  onChange={(e) => setRequestId(e.target.value)}
                  disabled={isBoxValidated}
                  fullWidth
                  variant="outlined"
                  margin="normal"
                  id="bulk-nats-request-id"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label={t('smartCatalog.bulkNatsBoxId')}
                  value={boxId}
                  onChange={(e) => setBoxId(e.target.value.toUpperCase())}
                  disabled={isBoxValidated}
                  fullWidth
                  variant="outlined"
                  margin="normal"
                  id="bulk-nats-box-id"
                />
              </Grid>
            </Grid>
            <Box style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8 }}>
              <ContainedButton
                id="bulk-nats-validate-box"
                onClick={handleValidateBox}
                disabled={isBoxValidated || isValidating || !normalizedRequestId || !normalizedBoxId}
              >
                {t('smartCatalog.bulkNatsValidateBox')}
              </ContainedButton>
              <ContainedButton
                id="bulk-nats-reset"
                onClick={handleReset}
                disabled={!isBoxValidated}
              >
                {t('smartCatalog.bulkNatsReset')}
              </ContainedButton>
            </Box>
            <Box mt={2} mb={2}>
              <ImageUploader
                allowedFormats={['JPG', 'JPEG']}
                images={images}
                setImages={setImages}
                maxSize={MAX_MB}
                maxImages={MAX_IMAGES}
                uploadTitleKey="smartCatalog.bulkNatsUploadHeading"
                disabled={!isBoxValidated}
              />
            </Box>
            {progress 
              ? <Typography variant="body2" style={{ marginBottom: 8 }}>
                {progress}
              </Typography>
              : null}
            <Box style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8 }}>
              <ContainedButton
                id="bulk-nats-save"
                onClick={handleSave}
                disabled={!isBoxValidated || !images.length}
              >
                {t('smartCatalog.bulkNatsSave')}
              </ContainedButton>
              <ContainedButton
                id="bulk-nats-close-box"
                onClick={handleOpenCloseBoxDialog}
                disabled={!isBoxValidated || totalImageCount === 0 || isClosingBox}
              >
                {t('buttons.closeBox')}
              </ContainedButton>
              <Typography variant="body2">
                {t('tableColumns.totalFileCount')}: {totalImageCount}
              </Typography>
            </Box>
            <Dialog open={closeDialogOpen} onClose={handleCloseBoxDialog}>
              <DialogTitle>{t('templates.ConfirmAction')}</DialogTitle>
              <DialogContent>
                <DialogContentText>{t('templates.closeBoxConfirm')}</DialogContentText>
              </DialogContent>
              <DialogActions>
                <Button onClick={handleCloseBoxDialog} color="primary">
                  {t('dialogs.cancel')}
                </Button>
                <Button onClick={handleConfirmCloseBox} color="primary">
                  {t('templates.confirm')}
                </Button>
              </DialogActions>
            </Dialog>
        </CardContent>
      </Card>
    </MainRouteSubRoutePage>
  )
}

export default LocalBulkActivityImageUpload
